import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { computeSimulation } from './priceSimulateCompute';
import { roundMoney } from './budgetEngineV2';
import { loadInitialConstants, loadConstantsFromWorkspace } from './priceSimulateStorage';
import { useTranslation } from '../../app/LanguageContext';
import { useWorkspace } from '../../app/WorkspaceContext';
import { getLanguage } from '../../app/i18n';
import {
  formatCurrency,
  formatNumberAsCurrencyMask,
  handleCurrencyInputChange,
  formatIntegerPercentInputFromNumber,
  handleIntegerPercentInputChange,
  formatPercent,
} from '../../app/currency';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

const selectOnFocus = (e) => {
  const el = e.target;
  queueMicrotask(() => {
    if (el && typeof el.select === 'function') el.select();
  });
};

const newVcRow = () => ({
  id: `vc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: '',
  cost_unit: 0,
});

const initialDeliveryName = () => (getLanguage() === 'pt-BR' ? 'Entrega' : 'Delivery');

const CurrencyField = ({ value, onChange, label, size = 'small', fullWidth, sx, disabled }) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const num = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const shown = focused ? text : formatNumberAsCurrencyMask(num);

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      label={label}
      value={shown}
      disabled={disabled}
      onFocus={(e) => {
        setFocused(true);
        setText(formatNumberAsCurrencyMask(num));
        selectOnFocus(e);
      }}
      onBlur={() => {
        setFocused(false);
        setText('');
      }}
      onChange={(e) => {
        handleCurrencyInputChange(e, (synth) => {
          if (typeof synth === 'string') {
            setText('');
            onChange(0);
          } else {
            setText(synth.target.value);
            const nv = synth.target.numericValue;
            onChange(typeof nv === 'number' && Number.isFinite(nv) ? nv : 0);
          }
        });
      }}
      sx={sx}
    />
  );
};

const PercentField = ({ value, onChange, label, size = 'small', fullWidth, sx }) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const num = typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  const shown = focused ? text : formatIntegerPercentInputFromNumber(num);

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      label={label}
      value={shown}
      onFocus={(e) => {
        setFocused(true);
        setText(formatIntegerPercentInputFromNumber(num));
        selectOnFocus(e);
      }}
      onBlur={() => {
        const raw = text;
        setFocused(false);
        setText('');
        const n = parseInt(raw.replace(/\D/g, ''), 10);
        onChange(Number.isFinite(n) && n >= 0 ? n : 0);
      }}
      onChange={(e) => {
        handleIntegerPercentInputChange(e, (synth) => {
          setText(synth.target.value);
          if (typeof synth.numericValue === 'number' && Number.isFinite(synth.numericValue)) {
            onChange(Math.max(0, Math.round(synth.numericValue)));
          }
        });
      }}
      sx={sx}
    />
  );
};

function ResultPanel({ translate, result, quantity }) {
  if (!result) return null;

  const q = Math.max(1, ni(quantity));
  const finalPrice = nf(result.pricing.final_price);
  const unitPrice = nf(result.pricing.unit_price);
  const totalCost = nf(result.composition.subtotal_before_marketing);
  const marginPct = nf(result.pricing.margin_pct_on_final);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'primary.main',
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.04)',
      }}
    >
      <Stack direction="row" spacing={2} justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {translate('price_simulate.summary_footer_cost')}
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(totalCost)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {translate('price_simulate.summary_fee_profit')} ({formatPercent(marginPct)}%)
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(nf(result.profit_margin.amount))}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {translate('price_simulate.summary_footer_price')}
          </Typography>
          <Typography variant="h5" fontWeight={700} color="primary" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {formatCurrency(finalPrice)}
          </Typography>
          {q > 1 && (
            <Typography variant="caption" color="text.secondary">
              {translate('price_simulate.unit_price')}: {formatCurrency(unitPrice)}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default function PriceSimulatorDialog({ open, onClose, initialDescription = '', initialQuantity = 1, onApplyPrice }) {
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();

  const [constants, setConstants] = useState(null);
  const [product, setProduct] = useState(null);
  const [result, setResult] = useState(null);
  const [simError, setSimError] = useState('');
  const [configOpen, setConfigOpen] = useState(false);

  const constantsRef = useRef(null);
  constantsRef.current = constants;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const base = loadInitialConstants();
        const merged = currentWorkspace?.id
          ? (await loadConstantsFromWorkspace(currentWorkspace.id)) ?? base
          : base;
        if (cancelled) return;
        setConstants(merged);
        const keys = Object.keys(merged.materials || {});
        const fee = nf(merged.pricing?.delivery_fee_default);
        const deliveryRow = fee > 0 ? [{ id: 'vc-delivery', name: initialDeliveryName(), cost_unit: fee }] : [];
        setProduct({
          description: initialDescription || '',
          quantity: Math.max(1, ni(initialQuantity) || 1),
          material: keys.includes('PLA') ? 'PLA' : keys[0] || 'PLA',
          grams: 0,
          print_hours: 0,
          modeling_hours: 0,
          painting_hours: 0,
          variable_costs: [...deliveryRow],
        });
        setResult(null);
        setSimError('');
      } catch (_) {
        if (!cancelled) {
          const base = loadInitialConstants();
          setConstants(base);
          const keys = Object.keys(base.materials || {});
          setProduct({
            description: initialDescription || '',
            quantity: Math.max(1, ni(initialQuantity) || 1),
            material: keys.includes('PLA') ? 'PLA' : keys[0] || 'PLA',
            grams: 0,
            print_hours: 0,
            modeling_hours: 0,
            painting_hours: 0,
            variable_costs: [],
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentWorkspace?.id, initialDescription, initialQuantity]);

  const payloadProduct = useMemo(() => {
    if (!product) return null;
    return {
      quantity: product.quantity,
      material: product.material,
      description: String(product.description ?? '').trim(),
      grams: nf(product.grams),
      print_hours: nf(product.print_hours),
      modeling_hours: nf(product.modeling_hours),
      painting_hours: nf(product.painting_hours),
      variable_costs: (product.variable_costs || []).map((r) => ({
        name: String(r.name || '').trim(),
        cost_unit: nf(r.cost_unit),
      })),
    };
  }, [product]);

  useEffect(() => {
    if (!constants || !payloadProduct) return;
    const r = computeSimulation(constants, payloadProduct);
    if (r.ok) {
      setResult(r.data);
      setSimError('');
    } else {
      setResult(null);
      setSimError(r.error || translate('common.error'));
    }
  }, [constants, payloadProduct, translate]);

  const updateConstants = (path, value) => {
    setConstants((c) => {
      if (!c) return c;
      const next = JSON.parse(JSON.stringify(c));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateVariableCost = (id, field, value) => {
    setProduct((p) => {
      const rows = [...(p.variable_costs || [])];
      const i = rows.findIndex((r) => r.id === id);
      if (i < 0) return p;
      const next = { ...rows[i], [field]: field === 'name' ? value : nf(value) };
      rows[i] = next;
      return { ...p, variable_costs: rows };
    });
  };

  const handleApply = useCallback(() => {
    if (!result || !onApplyPrice) return;
    const price = nf(result.pricing.final_price);
    onApplyPrice(price);
    onClose();
  }, [result, onApplyPrice, onClose]);

  if (!constants || !product) return null;

  const materialEntries = Object.entries(constants.materials || {});
  const finalPrice = result ? nf(result.pricing.final_price) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <CalculateOutlinedIcon color="primary" />
        <Box sx={{ flex: 1 }}>{translate('extensions.price_simulator.dialog_title')}</Box>
        <IconButton size="small" onClick={onClose} aria-label={translate('common.close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: 'auto' }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label={translate('price_simulate.description')}
              value={product.description ?? ''}
              onFocus={selectOnFocus}
              onChange={(e) => setProduct((p) => ({ ...p, description: e.target.value }))}
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              size="small"
              label={translate('price_simulate.quantity')}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={product.quantity}
              onFocus={selectOnFocus}
              onChange={(e) => setProduct((p) => ({ ...p, quantity: ni(e.target.value) || 1 }))}
              sx={{ width: 100, flexShrink: 0 }}
            />
            <FormControl size="small" sx={{ width: 140, flexShrink: 0 }}>
              <InputLabel>{translate('price_simulate.material')}</InputLabel>
              <Select
                label={translate('price_simulate.material')}
                value={product.material}
                onChange={(e) => setProduct((p) => ({ ...p, material: e.target.value }))}
              >
                {Object.keys(constants.materials || {}).map((k) => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label={translate('price_simulate.grams')}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={product.grams}
              onFocus={selectOnFocus}
              onChange={(e) => setProduct((p) => ({ ...p, grams: nf(e.target.value) }))}
            />
            <TextField
              size="small"
              fullWidth
              label={translate('price_simulate.print_hours')}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={product.print_hours}
              onFocus={selectOnFocus}
              onChange={(e) => setProduct((p) => ({ ...p, print_hours: nf(e.target.value) }))}
            />
            <TextField
              size="small"
              fullWidth
              label={translate('price_simulate.modeling_hours')}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={product.modeling_hours}
              onFocus={selectOnFocus}
              onChange={(e) => setProduct((p) => ({ ...p, modeling_hours: nf(e.target.value) }))}
            />
            <TextField
              size="small"
              fullWidth
              label={translate('price_simulate.painting_hours')}
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={product.painting_hours}
              onFocus={selectOnFocus}
              onChange={(e) => setProduct((p) => ({ ...p, painting_hours: nf(e.target.value) }))}
            />
          </Stack>

          {(product.variable_costs || []).length > 0 && (
            <Stack spacing={0.75}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {translate('price_simulate.variable_costs_section')}
              </Typography>
              {product.variable_costs.map((row) => (
                <Stack key={row.id} direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    size="small"
                    fullWidth
                    label={translate('price_simulate.variable_cost_name')}
                    value={row.name}
                    onFocus={selectOnFocus}
                    onChange={(e) => updateVariableCost(row.id, 'name', e.target.value)}
                  />
                  <CurrencyField
                    size="small"
                    label={translate('price_simulate.cost_unit')}
                    value={row.cost_unit}
                    onChange={(v) => updateVariableCost(row.id, 'cost_unit', v)}
                    sx={{ width: 140, flexShrink: 0 }}
                  />
                  <IconButton
                    size="small"
                    sx={{ mb: 0.5 }}
                    onClick={() => setProduct((p) => ({ ...p, variable_costs: p.variable_costs.filter((r) => r.id !== row.id) }))}
                    aria-label={translate('common.delete')}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}

          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setProduct((p) => ({ ...p, variable_costs: [...(p.variable_costs || []), newVcRow()] }))}
            >
              {translate('price_simulate.variable_cost_add')}
            </Button>
          </Box>

          <Divider />

          {simError && (
            <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
              {simError}
            </Alert>
          )}

          {result && (
            <ResultPanel translate={translate} result={result} quantity={product.quantity} />
          )}

          <Box>
            <Button
              size="small"
              variant="text"
              color="inherit"
              sx={{ opacity: 0.6, fontSize: '0.75rem' }}
              onClick={() => setConfigOpen((v) => !v)}
            >
              {configOpen ? translate('extensions.price_simulator.hide_config') : translate('extensions.price_simulator.show_config')}
            </Button>
          </Box>

          {configOpen && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {translate('price_simulate.section_config')}
              </Typography>
              <Stack spacing={1}>
                {materialEntries.map(([key, row]) => (
                  <Stack key={key} direction="row" spacing={1} alignItems="flex-end">
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{key}</Typography>
                    <CurrencyField
                      size="small"
                      label={translate('price_simulate.cost_per_kg')}
                      value={row.cost_per_kg}
                      onChange={(v) => {
                        setConstants((c) => {
                          const next = JSON.parse(JSON.stringify(c));
                          if (next.materials[key]) next.materials[key].cost_per_kg = v;
                          return next;
                        });
                      }}
                      sx={{ width: 160, flexShrink: 0 }}
                    />
                  </Stack>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <CurrencyField size="small" fullWidth label={translate('price_simulate.equipment_maintenance_hour')} value={constants.machine.equipment_maintenance_per_hour} onChange={(v) => updateConstants('machine.equipment_maintenance_per_hour', v)} />
                  <CurrencyField size="small" fullWidth label={translate('price_simulate.equipment_degradation_hour')} value={constants.machine.equipment_degradation_per_hour} onChange={(v) => updateConstants('machine.equipment_degradation_per_hour', v)} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <CurrencyField size="small" fullWidth label={translate('price_simulate.energy_hour')} value={constants.machine.energy_per_hour} onChange={(v) => updateConstants('machine.energy_per_hour', v)} />
                  <CurrencyField size="small" fullWidth label={translate('price_simulate.print_labor_hour')} value={constants.machine.print_labor_per_hour} onChange={(v) => updateConstants('machine.print_labor_per_hour', v)} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <CurrencyField size="small" fullWidth label={translate('price_simulate.modeling_hour')} value={constants.services.modeling_per_hour} onChange={(v) => updateConstants('services.modeling_per_hour', v)} />
                  <CurrencyField size="small" fullWidth label={translate('price_simulate.painting_hour')} value={constants.services.painting_per_hour} onChange={(v) => updateConstants('services.painting_per_hour', v)} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <PercentField size="small" fullWidth label={translate('price_simulate.failure_pct')} value={constants.percentages.failure_percent} onChange={(v) => updateConstants('percentages.failure_percent', v)} />
                  <PercentField size="small" fullWidth label={translate('price_simulate.marketing_pct')} value={constants.percentages.marketing_percent} onChange={(v) => updateConstants('percentages.marketing_percent', v)} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <PercentField size="small" fullWidth label={translate('price_simulate.reinvestment_pct')} value={constants.pricing.reinvestment_percent} onChange={(v) => updateConstants('pricing.reinvestment_percent', v)} />
                  <PercentField size="small" fullWidth label={translate('price_simulate.profit_margin_pct')} value={constants.pricing.profit_margin_percent} onChange={(v) => updateConstants('pricing.profit_margin_percent', v)} />
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          {translate('common.cancel')}
        </Button>
        {onApplyPrice && (
          <Tooltip title={!result ? translate('extensions.price_simulator.no_result_yet') : ''} disableHoverListener={!!result}>
            <span>
              <Button
                variant="contained"
                disabled={!result || !finalPrice}
                onClick={handleApply}
                startIcon={<CalculateOutlinedIcon />}
              >
                {finalPrice
                  ? translate('extensions.price_simulator.apply_price', { price: formatCurrency(finalPrice) })
                  : translate('extensions.price_simulator.apply_price_label')}
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
}
